import { useState, useEffect, useRef } from 'react';

const API = process.env.REACT_APP_API_BASE;

function fmtSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

export default function DocumentsPage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/documents/files`);
      setFiles(await r.json());
    } catch (e) {
      showToast('Lỗi tải danh sách: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) return showToast('Chọn file trước', 'error');
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch(`${API}/documents/upload`, { method: 'POST', body: fd });
      const res = await r.json();
      if (res.ok) {
        showToast(`Đã tải lên "${res.filename}"`);
        fileInputRef.current.value = '';
        load();
      } else {
        showToast(res.error || 'Lỗi', 'error');
      }
    } catch (e) {
      showToast('Lỗi upload: ' + e.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-10">
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Upload form */}
      <div className="bg-white rounded-2xl shadow p-5">
        <h2 className="font-bold text-gray-800 text-base mb-4">📤 Upload tài liệu</h2>
        <form onSubmit={handleUpload} className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-red-50 file:text-red-700"
          />
          <button
            type="submit"
            disabled={uploading}
            className="px-4 py-2 rounded-xl text-sm font-bold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 whitespace-nowrap"
          >
            {uploading ? '...' : '🚀 Upload'}
          </button>
        </form>
      </div>

      {/* File list */}
      <div className="bg-white rounded-2xl shadow p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs text-gray-400">{files.length} file</div>
          <button
            onClick={load}
            disabled={loading}
            className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:text-gray-700 text-base"
            title="Tải lại"
          >
            {loading ? '·' : '↻'}
          </button>
        </div>

        {files.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Chưa có file nào</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {files.map((f) => (
              <div key={f.name} className="py-2.5 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-700 truncate" title={f.name}>
                    📄 {f.name}
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(f.mtime).toLocaleDateString('vi-VN')} · {fmtSize(f.size)}
                  </div>
                </div>
                <a
                  href={`${API}/documents/data/${encodeURIComponent(f.name)}`}
                  download
                  className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-red-50 text-red-700 font-semibold hover:bg-red-100"
                >
                  ↓ Tải về
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
